/**
 * The disposal errors of the SDK's two lifecycles, and the typed failure a
 * cell subscription reports. A leaf module: `client.ts` exposes the classes
 * as `SheetHandle.DisposedError` and `LonaSdk.Client.DisposedError`, and the
 * cell handles (`CellHandle`, `RowRangeCellHandle`) narrow what they report
 * against them without importing the client.
 *
 * @internal SDK-private; the barrels export the classes only through those
 * two namespaces.
 */
import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { SdkRequestError } from "@tento-lona/sheets";

/**
 * `SheetHandle.DisposedError`: a fetch or write through a `SheetHandle`
 * after `handle.dispose()` (or the end of its `using` block). The handle is
 * out of the client's cache; reopen the sheet with `client.open(...)`.
 * `verb` names what was attempted.
 */
export class SheetHandleDisposedError extends Error {
  readonly verb: string;
  constructor(verb: string) {
    super(
      `SheetHandle.${verb} on a disposed handle; reopen the sheet with client.open`,
    );
    this.name = "SheetHandle.DisposedError";
    this.verb = verb;
  }
}

/**
 * `LonaSdk.Client.DisposedError`: a verb of a client — or of one of its
 * facades (`client.sheets`, `events`, `tasks`, `rows`, `calendars`,
 * `preferences`, `user`, `chats`, `themes`, also a reference taken before
 * the dispose) or of a row it opened — after `client.dispose()` (or the end
 * of its `using` block). Build a new client. `verb` names what was
 * attempted.
 */
export class ClientDisposedError extends Error {
  readonly verb: string;
  constructor(verb: string) {
    super(`LonaSdk.Client: ${verb} on a disposed client; build a new client`);
    this.name = "LonaSdk.Client.DisposedError";
    this.verb = verb;
  }
}

/** Assigned from `ClientLifecycle`'s static block. */
let bindRefusalsOf: (lifecycle: ClientLifecycle, handler: (error: SdkRequestError) => void) => void;
/** Assigned from `ClientLifecycle`'s static block. */
let refusalsOf: (lifecycle: ClientLifecycle) => Option<(error: SdkRequestError) => void>;

/**
 * `LonaSdk.Client.Lifecycle`: the one liveness a client shares with every
 * facade it builds (`sheets`, `rows`, `events`, `calendars`, `tasks`,
 * `user`, `chats`, `themes`) and every standalone row it opens. Each
 * network verb calls `assertLive(verb)` first, so a facade or row taken
 * before `client.dispose()` refuses with `LonaSdk.Client.DisposedError`
 * afterwards — before any optimistic change is applied. What outlives the
 * call — a standalone row's cell subscription (`row.cell`, `row.cells`) —
 * registers `onDisposed` and is failed by `dispose`, as a sheet's cell
 * subscriptions are by the sheet's. A facade built without a client takes
 * a fresh lifecycle (`new LonaSdk.Client.Lifecycle()`) and disposes it
 * itself, if ever.
 */
export class ClientLifecycle {
  static {
    bindRefusalsOf = (lifecycle, handler) => {
      lifecycle.#unobservedRefusals = handler;
    };
    refusalsOf = (lifecycle) => lifecycle.#unobservedRefusals;
  }

  #disposed = false;
  /** The owning client's `requestErrorHandler` for the durable refusals
   *  nobody read (`bindUnobservedRefusals`); `null` for a lifecycle no client
   *  built. */
  #unobservedRefusals: Option<(error: SdkRequestError) => void> = null;
  /** The holders `dispose` notifies (`onDisposed`), each once. */
  readonly #watchers = new Set<() => void>();

  /** True once `dispose` ran. */
  get disposed(): boolean {
    return this.#disposed;
  }

  /**
   * Retire every holder: each later `assertLive` throws, and each
   * `onDisposed` watcher still registered runs once. Idempotent. A watcher
   * that throws (a cell read's `onError` runs inside it) does not stop the
   * others: every watcher runs, then `dispose` throws one `AggregateError`
   * whose `errors` are the thrown values, in watcher order. The lifecycle
   * is disposed either way; a second call returns at once.
   */
  dispose(): void {
    if (this.#disposed) return;
    this.#disposed = true;
    const watchers = [...this.#watchers];
    this.#watchers.clear();
    const failures = new DisposeFailures();
    for (const watcher of watchers) failures.run(watcher);
    failures.rethrow("LonaSdk.Client.Lifecycle.dispose: a disposal watcher threw");
  }

  /**
   * Run `watcher` once when `dispose` runs — never when it already ran
   * (check `assertLive` first). Returns the unregister; call it when the
   * holder is released first.
   */
  onDisposed(watcher: () => void): Unsubscribe {
    if (this.#disposed) return () => {};
    const entry = (): void => watcher();
    this.#watchers.add(entry);
    return () => {
      this.#watchers.delete(entry);
    };
  }

  /** Throw `LonaSdk.Client.DisposedError` naming `verb` once `dispose` ran. */
  assertLive(verb: string): void {
    if (this.#disposed) throw new ClientDisposedError(verb);
  }
}

/**
 * Bind the client's `requestErrorHandler` to its lifecycle: the refusals of
 * the durable writes its verbs issue whose `settled` nobody read go to it
 * (`observedDurable`). Called once, by the client.
 */
export function bindUnobservedRefusals(lifecycle: ClientLifecycle, handler: (error: SdkRequestError) => void): void {
  bindRefusalsOf(lifecycle, handler);
}

/** The handler `bindUnobservedRefusals` bound to `lifecycle`, or `null`. */
export function unobservedRefusalsOf(lifecycle: ClientLifecycle): Option<(error: SdkRequestError) => void> {
  return refusalsOf(lifecycle);
}

/** The `AggregateError` `DisposeFailures.rethrow` throws, told apart so a
 *  nested teardown's failures are flattened into the outer one, not
 *  wrapped. Not exported: a host sees a plain `AggregateError`. */
class DisposeAggregateError extends AggregateError {
  constructor(errors: readonly unknown[], message: string) {
    super(errors, message);
    this.name = "AggregateError";
  }
}

/**
 * The failures of one disposal: `run` each stage (a watcher, a teardown
 * step), catching what it throws so the next still runs; `rethrow` after
 * the last. What a stage throws is kept as is, except an `AggregateError`
 * a nested `DisposeFailures` raised (the client's lifecycle inside
 * `client.dispose()`), whose `errors` are taken over one by one.
 */
export class DisposeFailures {
  readonly #errors: unknown[] = [];

  /** Run `stage`; record what it throws instead of propagating it. */
  run(stage: () => void): void {
    try {
      stage();
    } catch (err) {
      if (err instanceof DisposeAggregateError) {
        this.#errors.push(...err.errors);
      } else {
        this.#errors.push(err);
      }
    }
  }

  /** Throw one `AggregateError` (`message`) of every recorded failure, in
   *  order; nothing when every stage ran clean. */
  rethrow(message: string): void {
    if (this.#errors.length === 0) return;
    throw new DisposeAggregateError([...this.#errors], message);
  }
}

/**
 * What a cell subscription (`CellHandle.onError`, `RowRangeCellHandle`'s
 * `onError` and rejection) reports: the owning sheet's
 * `SheetHandle.DisposedError`, a standalone row's client's
 * `LonaSdk.Client.DisposedError`, or the failed read as an `SdkRequestError`.
 * The backends classify their request's own failure where they send it (a
 * rejected fetch is `"unanswered"`, a body that did not decode
 * `"malformed"`), so a read that failed with anything else failed in the
 * SDK's code, not the transport: it is reported as the
 * `GET /api/rows/{rowKey}/cells` request's `SdkRequestError` (boundary
 * `cells`, step `get-cells`) with outcome `"faulted"`, the thrown message as
 * its `details` and the thrown value as its `cause`.
 */
export function cellReadFailure(
  err: unknown,
): SdkRequestError | SheetHandleDisposedError | ClientDisposedError {
  if (
    err instanceof SdkRequestError || err instanceof SheetHandleDisposedError ||
    err instanceof ClientDisposedError
  ) {
    return err;
  }
  const error = new SdkRequestError({
    boundary: "cells",
    step: "get-cells",
    method: "GET",
    path: "/api/rows/{rowKey}/cells",
    failure: { outcome: "faulted" },
    details: err instanceof Error ? err.message : String(err),
  });
  error.cause = err;
  return error;
}
